// react
import { useCallback, useMemo, useState, type ReactNode } from 'react';

// components
import { Avatar } from '@/components/atoms/Avatar/Avatar';
import { Button } from '@/components/atoms/Button/Button';
import { Dialog } from '@/components/atoms/Dialog/Dialog';

import { ProfilePreview } from '@/components/molecules/ProfilePreview/ProfilePreview';

// utils
import { getProfilePictureUrl } from '@/pwa/utils/getProfilePictureUrl';
import { formatRoleName } from '@/utils/format';

import { InviteMembersByEmailForm } from '@/components/organisms/InviteMembersByEmailForm/InviteMembersByEmailForm';
import { InviteMembersForm } from '@/components/organisms/InviteMembersForm/InviteMembersForm';
import { RowAction } from '@/components/organisms/Table/Table';

import { TablePage } from '@/components/pages/TablePage/TablePage';
import {
  Account,
  AccountSummary,
  AddInviteKeys,
  InviteMemberRequest,
  Organization,
} from '@karehero/models';

// tanstack
import { createColumnHelper } from '@tanstack/react-table';

// columns
const columnHelper = createColumnHelper<AccountSummary>();

/**
 * EnterprisePortal shows the accounts and invited members of an organization
 */
export const EnterprisePortal = ({
  organization,
  accounts,
  inviteMembers,
  addInviteKeys,
  notifyToast,
  resendInvite,
  resendEmailInvites,
  onBack,
  isAdmin,
  isTitleHidden,
}: EnterprisePortalProps) => {
  // state
  const [isProfilePreviewOpen, setIsProfilePreviewOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<Account | null>(null);
  const [isShowInviteMembers, setIsShowInviteMembers] = useState(false);
  const [isShowAddInviteKeys, setIsShowAddInviteKeys] = useState(false);
  const [isShowAddOrganizationAdmin, setIsShowAddOrganizationAdmin] =
    useState(false);
  const [isShowSelfReferral, setIsShowSelfReferral] = useState(false);

  // memo
  const columns = useMemo(
    () => [
      columnHelper.accessor('imgSrc', {
        header: '',
        cell: (info) => {
          const row = info.cell.row.original;
          let fallback =
            `${row.firstName?.[0] || ''}${row.lastName?.[0] || ''}`.toUpperCase();
          if (fallback.length === 0) {
            if (row.email && row.email.length > 0) {
              fallback = row.email[0].toUpperCase();
            } else {
              row.inviteKey?.[0]?.toUpperCase() || '';
            }
          }
          return (
            <Avatar
              size='xs'
              src={getProfilePictureUrl(row)}
              fallback={fallback}
            />
          );
        },
      }),
      columnHelper.accessor('fullName', {
        header: 'Name',
      }),
      columnHelper.accessor('email', {
        header: 'Email',
      }),
      ...(organization?.isInviteKeyEnabled
        ? [
            columnHelper.accessor('inviteKey', {
              header: organization.inviteKeyName || 'Payroll ID',
            }),
          ]
        : []),
      columnHelper.accessor('isRegistered', {
        header: 'Registered',
        cell: (info) => {
          return info.getValue() ? 'Yes' : 'No';
        },
      }),
      columnHelper.accessor('accountRole.name', {
        header: 'Account Role',
        cell: (info) => {
          return formatRoleName(info.getValue()) || 'Member';
        },
      }),
    ],
    [organization],
  );

  const selectedProfileInitials = useMemo(() => {
    if (!selectedProfile) return '';
    return (
      (selectedProfile?.firstName?.[0]?.toUpperCase() || '') +
      (selectedProfile?.lastName?.[0]?.toUpperCase() || '')
    );
  }, [selectedProfile]);

  const actions = useMemo<RowAction[]>(() => {
    return [
      {
        label: 'Reinvite',
        isHidden: (account: AccountSummary) =>
          !!account.isRegistered || (account.email?.length || 0) === 0,
        onPress: (account: AccountSummary) => {
          if (account) {
            resendInvite(account.id, account.email);
          }
        },
      },
      {
        label: 'View',
        isHidden: (account: AccountSummary) => !account.isRegistered,
        onPress: (account: AccountSummary) => {
          const accountAsAccount: Account = {
            ...account,
            email: account.email || '',
          };
          setSelectedProfile(accountAsAccount);
          setIsProfilePreviewOpen(true);
        },
      },
    ];
  }, [inviteMembers, notifyToast]);

  // callback
  const submitMembers = useCallback(
    (req: AddInviteKeys) => {
      const { inviteKeys } = req;
      inviteMembers({ emails: inviteKeys });
      setIsShowInviteMembers(false);
      notifyToast({
        title: 'Invitation Success',
        description: `Sent invitations to ${inviteKeys.slice(0, 3).join(', ')}${
          inviteKeys.length > 3 ? `, and ${inviteKeys.length - 3} more` : ''
        }`,
      });
    },
    [inviteMembers, notifyToast],
  );

  const submitInviteKeys = useCallback(
    (req: AddInviteKeys) => {
      const { inviteKeys } = req;
      addInviteKeys(req);
      setIsShowAddInviteKeys(false);
      notifyToast({
        title: 'Invitation Success',
        description: `Added ${organization?.inviteKeyName}s for ${inviteKeys
          .slice(0, 3)
          .join(
            ', ',
          )}${inviteKeys.length > 3 ? `, and ${inviteKeys.length - 3} more` : ''}`,
      });
    },
    [addInviteKeys, notifyToast],
  );

  return (
    <>
      <TablePage
        title={`${organization?.name} Invite Portal`}
        columns={columns}
        rows={accounts}
        actions={actions}
        onBack={onBack}
        isTitleHidden={isTitleHidden}
        toolbarActions={
          <>
            <Button
              ariaLabel='invite new members'
              size='sm'
              onPress={() => setIsShowInviteMembers(true)}
              iconLeft={'plus'}
              isRectangle
            >
              Invite New Members
            </Button>
            {organization?.isInviteKeyEnabled ? (
              <Button
                ariaLabel='upload invite keys'
                size='sm'
                onPress={() => setIsShowAddInviteKeys(true)}
                iconLeft={'plus'}
                isRectangle
              >
                Upload {organization?.inviteKeyName}s
              </Button>
            ) : null}
            {isAdmin && (
              <Button
                iconLeft='paper-plane-top'
                ariaLabel='Resend All Pending Invites'
                size='sm'
                isRectangle
                onPress={resendEmailInvites}
              >
                Resend All Pending Invites
              </Button>
            )}
          </>
        }
      />
      <Dialog
        title='Invite Members'
        isCloseButton
        isOpen={isShowInviteMembers}
        setIsOpen={setIsShowInviteMembers}
      >
        <InviteMembersByEmailForm
          onSubmit={submitMembers}
          organization={organization}
        />
      </Dialog>
      <Dialog
        title={`Upload ${organization?.inviteKeyName}s`}
        isCloseButton
        isOpen={isShowAddInviteKeys}
        setIsOpen={setIsShowAddInviteKeys}
      >
        <InviteMembersForm
          onSubmit={submitInviteKeys}
          organization={organization}
          isValid={() => true}
          inputType='text'
          inviteKeyName={organization?.inviteKeyName || ''}
        />
      </Dialog>
      <Dialog
        variant='sheet'
        isOpen={isProfilePreviewOpen}
        setIsOpen={setIsProfilePreviewOpen}
      >
        <ProfilePreview
          imageSrc={getProfilePictureUrl(selectedProfile)}
          fallback={selectedProfileInitials}
          name={`${selectedProfile?.firstName || ''} ${
            selectedProfile?.lastName || ''
          }`}
          email={selectedProfile?.email}
          role={formatRoleName(selectedProfile?.accountRole?.name)}
          onClose={() => setIsProfilePreviewOpen(false)}
          isActionsDisabled
        />
      </Dialog>
    </>
  );
};

interface EnterprisePortalProps {
  organization?: Organization;
  accounts?: AccountSummary[];
  inviteMembers: (req: InviteMemberRequest) => void;
  addInviteKeys: (req: AddInviteKeys) => void;
  notifyToast: (req: { title: string; description: string }) => void;
  /** Resends the invite; the caller reports the outcome. */
  resendInvite: (id: string, email?: string) => void;
  resendEmailInvites: () => void;
  onBack?: () => void;
  isAdmin?: boolean;
  isTitleHidden?: boolean;
}

EnterprisePortal.displayName = 'EnterprisePortal';

export default EnterprisePortal;
